import {
  Box,
  Button,
  Center,
  HStack,
  ScaleFade,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  VStack,
  Flex,
  Switch,
  Badge,
} from '@chakra-ui/react';
import { CQTreeTable, TextInput, PrimaryButton } from '@laxmimanogna/code-quick-components';
import { createColumnHelper, sortingFns } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppColors from '../../../constants/AppColors';
import ROUTES from '../../../constants/Routes';
import { format } from 'date-fns';
import { replaceRoute } from '../../../utils/common.utils';
import { defaultTreeTableProp, paginationProps } from './table_config/cq_tree_table_props';
import { EyeAvatarIcon } from '../../../constants/IconData';
import AuditSheetFileViewer from '../../audit_sheet/components/AuditSheetFileViewer';
import { getBatchStatus } from '../../my_charts/components/tables/table_config';
import { BatchStatusIndicator, ChartStatusBadge, NotesCell, AttachmentCell } from '../../my_charts/components/tables/table_config';

const columnHelper = createColumnHelper();

const UploadBreakdownTable = ({
  data,
  isLoading,
  pagination,
  onPageChangeByNumber,
  onSizeChange,
  onPreviousPage,
  onNextPage,
  onSort,
  refetch,
  tableRef,
}) => {
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState('');

  const handleReviewToggle = async (rowData) => {
    try {
      // await physicianRepository.updateAccuracyReviewStatus({
      //   ...rowData,
      //   review: !rowData.review,
      // });
      refetch();
    } catch (error) {
      console.error('Error toggling review status:', error);
    }
  };

  // Transform the data to match CQTreeTable format
  const transformedData = React.useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.map(batch => ({
      ...batch,
      subRows: batch.children?.map(chart => ({
        ...chart,
        subRows: chart.encounters?.items || []
      })) || []
    }));
  }, [data]);

  // Sub-header styling for chart and encounter levels
  const CHART_SUBHEADER = {
    bg: '#E0F2FE',
    color: '#0369A1',
  };

  const ENCOUNTER_SUBHEADER = {
    bg: '#F1F5F9',
    color: '#64748B',
  };

  const columns = React.useMemo(
  () => [
    // ── Col 1: Batch ID / Encounter ──────────────────────────────────────────
    columnHelper.accessor('batch_id', {
      id: 'batch_id',
      header: () => 'Batch',
      sortValue: (row) => row.encounter || row.batch_id || '',
      subHeader: {
        1: { label: 'Encounter', ...ENCOUNTER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const row = info.row.original;

        const effectiveStatus =
          depth === 0 ? getBatchStatus(row.subRows) : row.status;

        const isCompleted = effectiveStatus === 'completed';

        // value for both levels
        const value =
          depth === 0
            ? (row?.batch_name ? row.batch_name : row?.batch_id)
            : row.encounter;

        const isTruncated = value?.length > 10;
        const truncatedValue = isTruncated
          ? `${value.slice(0, 10)}...`
          : value;

        // -------- DEPTH 0 (BATCH) --------
        if (depth === 0) {
          return (
            <Tooltip
              hasArrow
              backgroundColor={!isCompleted ? AppColors.secondary : undefined}
              placement="bottom"
              label={
                isCompleted
                  ? (isTruncated ? value : '')
                  : (
                      <>
                        Batch pending review.
                        {isTruncated && (
                          <>
                            <br />
                            {value}
                          </>
                        )}
                      </>
                    )
              }
            >
              <Text
                whiteSpace="nowrap"
                _hover={isCompleted ? { textDecoration: 'underline' } : {}}
                cursor="pointer"
                onClick={() => {
                  if (!isCompleted) return;

                  const batchDashboard = replaceRoute(
                    ROUTES.BATCH_DASHBOARD,
                    { batchId: row.batch_id }
                  );
                  navigate(batchDashboard, { state: { chartData: row } });
                }}
                fontWeight={600}
                color="blue.800"
                fontSize="14px"
                textAlign="start"
              >
                {truncatedValue}
              </Text>
            </Tooltip>
          );
        }

        // -------- DEPTH 1 (ENCOUNTER) --------
        return (
          <Tooltip
            hasArrow
            placement="bottom"
            label={isTruncated ? value : ''}
          >
            <Text
              fontWeight={400}
              whiteSpace="nowrap"
              textAlign="start"
              color="gray.600"
              fontSize="13px"
            >
              {truncatedValue}
            </Text>
          </Tooltip>
        );
      },
    }),

    // ── Col 2: Upload Date / File ────────────────────────────────────────────
    columnHelper.accessor('upload_date', {
      id: 'upload_date',
      header: () => 'Upload Date',
      sortValue: (row) => row.enc_date || '',
      subHeader: {
        // "File" replaces what was "Enc. Date" at depth 2
        1: { label: 'File', ...ENCOUNTER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const row = info.row.original;

        if (depth === 0) {
          const val = info.getValue();
          return val ? (
            <Text textAlign="center" fontSize="14px">{format(new Date(val), 'MM/dd/yyyy')}</Text>
          ) : (
            <Text color="gray.400" fontSize="14px">N/A</Text>
          );
        }

        // depth === 1 → File name (was enc_date at depth 2, now shows file)
        const fileName = row.chart_id;
        return fileName ? (
          <Text fontSize="12px" textAlign="center" color="gray.600" noOfLines={1} maxW="140px">
            {fileName}
          </Text>
        ) : (
          <Text color="gray.400" fontSize="13px">N/A</Text>
        );
      },
    }),

    // ── Col 3: Completed Date / Attachments ──────────────────────────────────
    columnHelper.accessor('completed_date', {
      id: 'completed_date',
      header: () => 'Completed Date',
      sortValue: (row) => row.completed_date || '',
      subHeader: {
        // "Attachments" replaces what was "Notes" at depth 2
        1: { label: 'Attachments', ...ENCOUNTER_SUBHEADER },
      },
      cell: info => {
        const depth = info.row.depth;
        const row = info.row.original;

        if (depth === 0) {
          const val = info.getValue();
          const effectiveStatus = getBatchStatus(row.subRows);
          if (!val || effectiveStatus !== 'completed') {
            return <Text color="gray.400" fontSize="14px" textAlign="center">N/A</Text>;
          }
          return <Text textAlign="center" fontSize="14px">{format(new Date(val), 'MM/dd/yyyy')}</Text>;
        }

        // depth === 1 → Attachments (file_obj + audited_chart + attachments[])
        const attachmentNames = [
          row.file_obj?.name,
          row.file_obj?.audited_chart_name,
          ...(row.attachments?.map(a => a.file.name) ?? [])
        ].filter(Boolean);

        return <AttachmentCell attachments={attachmentNames} />;
      },
    }),

    // ── Col 4: Status / Error ─────────────────────────────────────────────────
    columnHelper.accessor('cq_score', {
      id: 'cq_score',
      header: () => 'RI Score',
      sortValue: (row) => row.status || row.error || '',
      subHeader: {
        1: { label: 'Error', ...ENCOUNTER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const row = info.row.original;

        if (depth === 0) {
          return <Text textAlign="center" fontSize="14px">{row.cq_score}</Text>
        }

        // depth === 1 → Error
        return row.error ? (
          <Text fontSize="13px" textAlign="center">{row.error}</Text>
        ) : (
          <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>
        );
      },
    }),

    // ── Col 5: Total Encounters / Suggestion ──────────────────────────────────
    columnHelper.accessor(row => row, {
      id: 'charts_encounters',
      header: () => 'Encounters',
      sortValue: (row) => row.no_of_encounters || '',
      subHeader: {
        1: { label: 'Suggestion', ...ENCOUNTER_SUBHEADER },
      },
      cell: info => {
        const depth = info.row.depth;
        const row = info.getValue();

        if (depth === 0) {
          return row.no_of_encounters != null ? (
            <Text textAlign="center" fontSize="14px">{row.no_of_encounters}</Text>
          ) : (
            <Text color="gray.400" fontSize="14px">N/A</Text>
          );
        }

        // depth === 1 → Suggestion
        return row.suggestion ? (
          <Text fontSize="13px" textAlign="center">{row.suggestion}</Text>
        ) : (
          <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>
        );
      },
    }),

    // ── Col 6: Provider / Provider ────────────────────────────────────────────
    columnHelper.accessor(row => row, {
      id: 'providers',
      header: () => 'Provider',
      enableSorting: false,
      subHeader: {
        1: { label: 'Provider', ...ENCOUNTER_SUBHEADER },
      },
      cell: info => {
        const depth = info.row.depth;
        const rowVal = info.getValue();

        if (depth === 0) {
          const providers = rowVal.providers || [];
          if (providers.length === 0) return <Text color="gray.400" fontSize="14px" textAlign="center">—</Text>;
          if (providers.length === 1) return <Text fontSize="14px" textAlign="center">{providers[0]}</Text>;
          return (
            <Tooltip label={providers.join(', ')} placement="top" hasArrow>
              <Text fontSize="14px" cursor="help" textAlign="center">
                {providers[0]} <Text as="span" color="gray.500">+{providers.length - 1}</Text>
              </Text>
            </Tooltip>
          );
        }

        // depth === 1 → provider (string)
        return rowVal.provider ? (
          <Text fontSize="13px" textAlign="center">{rowVal.provider}</Text>
        ) : (
          <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>
        );
      },
    }),

    // ── Col 7: Opportunity / Opportunity ──────────────────────────────────────
    columnHelper.accessor('opportunity', {
      id: 'opportunity',
      header: () => 'Opportunity',
      subHeader: {
        1: { label: 'Opportunity', ...ENCOUNTER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        if (val === 0) return <Text color="gray.400" fontSize="14px" textAlign="center">0</Text>;
        return val != null ? (
          <Text textAlign="center" fontSize="14px">{val}</Text>
        ) : (
          <Text color="gray.400" fontSize="14px" textAlign="center">N/A</Text>
        );
      },
    }),

    // ── Col 8: Action ─────────────────────────────────────────────────────────
    columnHelper.accessor(row => row.upload_id, {
      id: 'action',
      header: () => '',
      enableSorting: false,
      subHeader: { 1: null },
      cell: info => {
        const depth = info.row.depth;
        const row = info.row.original;

        const effectiveStatus = depth === 0 ? getBatchStatus(row.subRows) : row.status;
        const isCompleted = effectiveStatus === 'completed';

        if (depth === 0) {
          // Batch level — View Batch button + eye icon
          return (
            <HStack justifyContent="start" spacing={2}>
              <Tooltip
                label={!isCompleted ? 'Available once reviewed.' : ''}
                isDisabled={isCompleted}
                placement="top"
                hasArrow
              >
                <Box>
                  <PrimaryButton
                    bg={AppColors.secondary}
                    fontSize="14px"
                    fontWeight="normal"
                    size="sm"
                    borderRadius="3xl"
                    px={5}
                    isDisabled={!isCompleted}
                    _hover={{ transition: 'all .1s ease', bgColor: AppColors.white, color: AppColors.secondary, outline: `2px solid ${AppColors.secondary}` }}
                    onClick={() => {
                      if (!isCompleted) return;
                      navigate(ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', row.batch_id));
                    }}
                  >
                    View Batch
                  </PrimaryButton>
                </Box>
              </Tooltip>
            </HStack>
          );
        }

        // depth === 1 → Encounter level (was depth 2)
        return (
          <HStack justifyContent="start" spacing={2}>
            <PrimaryButton
              bg={AppColors.secondary}
              fontSize="14px"
              fontWeight="normal"
              size="sm"
              borderRadius="3xl"
              px={5}
              _hover={{ transition: 'all .1s ease', bgColor: AppColors.white, color: AppColors.secondary, outline: `2px solid ${AppColors.secondary}` }}
              onClick={() => {
                let routeFormat = {
                  uploadId: row.id,
                  rendererId: row.provider_id,
                };
                let route = ROUTES.VIEW_RENDERRER_SINGLE_AUDIT_SHEET;
                navigate(`${replaceRoute(route, routeFormat)}/?location=${row.location}&filterByDate=${true}`);
              }}
            >
              Encounter
            </PrimaryButton>
            {/* Eye icon moved from chart level to batch level */}
              <AuditSheetFileViewer
                auditData={row}
                icon={<EyeAvatarIcon style={{ cursor: 'pointer' }} />}
                chart_id={row.id}
              />
          </HStack>
        );
      },
    }),
  ],
  [handleReviewToggle]
);

  useEffect(() => {
    onSort?.(ordering);
  }, [ordering]);

  return (
    <Box id="fixedTableHeader" className="tableWraper" sx={{
    maxHeight: '700px',   // adjust as needed
    overflowY: 'auto'
  }}>
      <CQTreeTable
        ref={tableRef}
        data={transformedData}
        columns={columns}
        getSubRows={(row) => row.subRows}
        manualPagination={false}
        manualSortBy={false}
        isLoading={isLoading}
        {...defaultTreeTableProp}
        {...paginationProps(pagination)}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
        onPreviousPage={onPreviousPage}
        onNextPage={onNextPage}
        onSizeChange={onSizeChange}
        onPageChangeByNumber={onPageChangeByNumber}
      />
      {!isLoading && (!data || data?.length === 0) && (
        <Center py={8}>
          <Text color={AppColors.gray}>There is no data to display</Text>
        </Center>
      )}
    </Box>
  );
};

export default UploadBreakdownTable;